import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const moneySchema = z.string().regex(/^-?(0|[1-9][0-9]*)\.[0-9]{4}$/).refine((value) => value !== '-0.0000');
const currencySchema = z.string().regex(/^[A-Z]{3}$/);
const datetimeSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)));
const coverageSchema = z.object({ state: z.enum(['complete', 'partial_source', 'unavailable']), partial_source: z.boolean(), label: z.string().nullable() }).strict();

export const ruleMatchSnapshotSchema = z.object({
  match_snapshot_id: uuidSchema,
  rule_id: uuidSchema,
  rule_version: z.number().int().positive(),
  state: z.enum(['current', 'rebuilding', 'failed', 'partial']),
  expires_at: datetimeSchema,
  coverage: coverageSchema,
  match_count: z.number().int().nonnegative().nullable(),
  known_match_count: z.number().int().nonnegative(),
  totals: z.record(currencySchema, moneySchema),
  examples: z.array(z.object({ line_item_id: uuidSchema.nullable(), version: z.number().int().positive().nullable(), transaction_id: uuidSchema.nullable(), posted_at: datetimeSchema, merchant_display: z.string().nullable(), amount: z.string(), currency: currencySchema.nullable(), write_context: z.string().nullable() }).strict()).max(20),
  exclusions: z.array(z.string()).max(50),
}).strict();
export type RuleMatchSnapshot = z.infer<typeof ruleMatchSnapshotSchema>;

export const ruleApplyInputSchema = z.object({
  rule_id: uuidSchema,
  operation_id: uuidSchema,
  match_snapshot_id: uuidSchema,
  expected_rule_version: z.number().int().positive(),
  acknowledge_partial_source: z.boolean(),
}).strict();
export type RuleApplyInput = z.infer<typeof ruleApplyInputSchema>;

export const ruleApplyResponseSchema = z.object({ job_id: uuidSchema, batch_id: uuidSchema, status: z.string().min(1) }).strict();
export type RuleApplyResponse = z.infer<typeof ruleApplyResponseSchema>;

export class RuleApplyConflictError extends Error {
  constructor(message: string) { super(message); this.name = 'RuleApplyConflictError'; }
}

export function useRuleMatchSnapshot(ruleId: string | null | undefined) {
  return useQuery({
    queryKey: ['rules', ruleId, 'snapshot'],
    enabled: Boolean(ruleId),
    queryFn: async () => {
      const response = await client.get(`/api/rules/${uuidSchema.parse(ruleId)}/matches`);
      return parseEnvelope(ruleMatchSnapshotSchema, response.data).data;
    },
    retry: false,
  });
}

export function useApplyRuleSnapshot() {
  const queryClient = useQueryClient();
  return useMutation<RuleApplyResponse, Error, RuleApplyInput>({
    mutationFn: async (input) => {
      const parsed = ruleApplyInputSchema.parse(input);
      try {
        const response = await client.post(`/api/rules/${parsed.rule_id}/apply`, {
          operation_id: parsed.operation_id,
          match_snapshot_id: parsed.match_snapshot_id,
          expected_rule_version: parsed.expected_rule_version,
          acknowledge_partial_source: parsed.acknowledge_partial_source,
        });
        return parseEnvelope(ruleApplyResponseSchema, response.data).data;
      } catch (error) {
        if (axios.isAxiosError(error) && isErrorEnvelope(error.response?.data) && error.response.data.error.code === 'changed_since') {
          throw new RuleApplyConflictError(error.response.data.error.message);
        }
        throw error;
      }
    },
    retry: false,
    onSuccess: (result, input) => {
      queryClient.invalidateQueries({ queryKey: ['rules', input.rule_id, 'snapshot'] });
      queryClient.invalidateQueries({ queryKey: ['jobs', result.job_id] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}
